import { RadioGroup } from "@headlessui/react";
import { captureException, withScope } from "@sentry/react";
import clsx from "clsx";
import { useAtom } from "jotai";
import { FC, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/Interface/Button/Button";
import { Combobox } from "@/components/Interface/FormFields/Combobox/Combobox";
import { RadioIndicator } from "@/components/Interface/FormFields/RadioButton/RadioButton";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { CountrylistDialog } from "@/components/Portal/DeviceManagement/Roaming/AddRoamingOption/CountryList/CountryList";
import { RoamingBookingPage } from "@/components/Portal/DeviceManagement/Roaming/components/RoamingBookingPage";
import {
  isYearlyRoamingPackage,
  optionBasketAtom,
} from "@/components/Portal/DeviceManagement/Roaming/roamingUtils";
import type { Country } from "@/constants/countries";
import { useGetSortedCountries } from "@/hooks/useGetSortedCountries";
import { useHandleError } from "@/hooks/useHandleError";
import { useSimIdFromQueryParam } from "@/hooks/useSimIdFromQueryParam";
import {
  orderRoamingPackage,
  useGetAvailableSunriseRoamingPackages,
} from "@/services/api";
import {
  GetAvailableSunriseRoamingPackagesPackageType,
  RoamingInfoUsageZone,
} from "@/services/model";
import { formatRoamingVolume, stripFlatName } from "@/utils/deviceUtils";
import { capitalizeFirstLetter, getCurrency } from "@/utils/translationHelpers";

/**
 * Derives the roaming zone from a package shortName.
 * Zone 1 packages contain "EU/USA" or "EU" in their name.
 */
const getZoneFromShortName = (
  shortName?: string,
): RoamingInfoUsageZone | undefined => {
  if (!shortName) {
    return undefined;
  }
  // Zone 1 packages contain "EU/USA" or similar EU zone identifiers
  if (shortName.includes("EU/USA") || shortName.includes("EU")) {
    return RoamingInfoUsageZone.ZONE1;
  }

  // Default to Zone 2/3 for other packages (will display in MB)
  return RoamingInfoUsageZone.ZONE2;
};

type RoamingOptionsForm = {
  selectedCountry: Country | null;
  selectedRateId: number | null;
};

export const AddRoamingOption: FC = () => {
  const [isFetchingOrder, setIsFetchingOrder] = useState(false);
  const [, setOptionBasket] = useAtom(optionBasketAtom);
  const [simId] = useSimIdFromQueryParam();
  const { t: portalT } = useTranslation("portal");
  const { t: tAdd } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.roaming.addOption",
  });
  const { t: translationT } = useTranslation("translation");
  const handleError = useHandleError();
  const navigate = useNavigate();
  const sortedCountries = useGetSortedCountries(true);
  const { watch, control, setValue, handleSubmit } =
    useForm<RoamingOptionsForm>({
      defaultValues: { selectedCountry: null, selectedRateId: null },
    });

  const selectedCountry = watch("selectedCountry");
  const selectedRateId = watch("selectedRateId");

  const {
    data: availableRoamingPackages,
    isFetching: isFetchingAvailableRoamingPackages,
  } = useGetAvailableSunriseRoamingPackages(
    parseInt(simId),
    {
      packageType: GetAvailableSunriseRoamingPackagesPackageType.Data_Roaming,
      country: selectedCountry?.ALPHA2,
    },
    {
      query: {
        keepPreviousData: true,
        onSuccess(data) {
          const selectedRateIdInNewData = data.subscriberPackagesRoaming?.some(
            (subscriberPackage) => subscriberPackage.id === selectedRateId,
          );

          // we clear the selectedRateId if the new fetched roaming packages
          // don't contain it.
          if (!selectedRateIdInNewData) {
            setValue("selectedRateId", null);
          }
        },
      },
    },
  );

  const subscriberPackages =
    availableRoamingPackages?.subscriberPackagesRoaming;

  const showNoPackagesForDestination =
    !!selectedCountry &&
    !isFetchingAvailableRoamingPackages &&
    !subscriberPackages?.length;

  const cancel = () => {
    navigate(`../display?simId=${simId}`, { replace: true });
  };

  const confirm = () => {
    // Pull a new basket and add it to the Atom
    if (typeof selectedRateId === "number") {
      setIsFetchingOrder(true);
      orderRoamingPackage(Number(simId), {
        optionId: selectedRateId,
      })
        .then((data) => {
          setIsFetchingOrder(false);
          if (!data?.basket) {
            handleError("Empty basket response from orderRoamingPackage");

            return;
          }
          setOptionBasket(data.basket);
          navigate(`../checkout?simId=${simId}`, { replace: true });
        })
        .catch((error) => {
          setIsFetchingOrder(false);
          if (
            `${error?.response?.data}`.includes(
              "Open roaming package order for SIM",
            )
          ) {
            handleError(
              "Open roaming package order for SIM",
              tAdd("errorWait"),
            );
          } else {
            handleError(error);
          }
          withScope(function (scope) {
            scope.setFingerprint(["/customer/contracts"]);
            scope.setTag("section", "portal");
            scope.setTag("domain", "AddRoamingOption");
            scope.setTag("endpoint", "addContract");
            captureException(error);
          });
        });
    }
  };

  return (
    <RoamingBookingPage
      dataTestid="add-roaming-option"
      title={tAdd("title")}
      step={1}
      stepLabel={tAdd("stepLabel")}
      onBack={cancel}
    >
      <form
        className="flex w-full flex-col gap-6"
        onSubmit={handleSubmit(confirm)}
      >
        <ProfileCard
          dataTestid="roaming-packages-card"
          variant="white"
          title={tAdd("packagesTitle")}
          subtitle={
            <Trans
              t={portalT}
              components={[<CountrylistDialog key={0} />]}
              i18nKey="cockpit.managementTile.roaming.addOption.countryList"
            />
          }
        >
          {(isFetchingOrder || isFetchingAvailableRoamingPackages) && (
            <LoadingSpinner />
          )}

          <p className="text-body-sm leading-none text-foreground-primary">
            {tAdd("selectCountryHint")}
          </p>

          <Controller
            control={control}
            name="selectedCountry"
            render={({ field }) => (
              <Combobox<Country>
                displayValue={(country: Country) =>
                  country && "ALPHA3" in country
                    ? translationT(`countries.${country.ALPHA3}`)
                    : ""
                }
                className="w-full tablet:max-w-sm"
                label={tAdd("countryDropdownLabel")}
                field={field}
                dataTestId="countries"
                options={sortedCountries}
                placeholder={tAdd("countryDropdownPlaceholder")}
              />
            )}
          />

          <div className="flex w-full items-center gap-4">
            <span className="h-px flex-1 bg-border-tertiary" />
            <span className="text-body-sm leading-none text-foreground-secondary">
              {tAdd("or")}
            </span>
            <span className="h-px flex-1 bg-border-tertiary" />
          </div>

          {!showNoPackagesForDestination && (
            <p className="text-body-sm leading-none text-foreground-primary">
              {tAdd("selectPackageHint")}
            </p>
          )}

          <div className="flex w-full flex-col">
            <div className="hidden w-full rounded-md bg-surface-quaternary tablet:flex">
              <span className="flex h-16 flex-1 items-center px-3 text-body-sm font-semibold leading-none text-foreground-primary">
                {tAdd("rate")}
              </span>
              <span className="flex h-16 flex-1 items-center px-3 text-body-sm font-semibold leading-none text-foreground-primary">
                {tAdd("volume")}
              </span>
              <span className="flex h-16 flex-1 items-center px-3 text-body-sm font-semibold leading-none text-foreground-primary">
                {tAdd("price")}
              </span>
              <span className="w-14" />
            </div>

            {showNoPackagesForDestination ? (
              <p className="px-3 py-6 text-body-sm leading-md text-foreground-primary">
                {tAdd("noPackagesForDestination")}
              </p>
            ) : (
              <RadioGroup
                value={selectedRateId}
                onChange={(value: number) => {
                  setValue("selectedRateId", value);
                }}
              >
                {subscriberPackages?.map(
                  (
                    {
                      id,
                      shortName,
                      initialAmount,
                      minContractDur,
                      minContractDurUnit,
                      price,
                    },
                    index,
                  ) => (
                    <RadioGroup.Option
                      key={shortName}
                      data-testid={`roaming-package-${id}`}
                      className="cursor-pointer"
                      value={id}
                    >
                      {({ checked }) => (
                        <div
                          className={clsx(
                            "flex w-full flex-wrap items-center gap-y-2 py-4 tablet:flex-nowrap tablet:gap-y-0 tablet:py-0",
                            index !== (subscriberPackages?.length ?? 0) - 1 &&
                              "border-b border-border-tertiary",
                          )}
                        >
                          <span className="order-1 w-1/2 px-3 text-body-base font-semibold leading-none text-foreground-primary tablet:order-none tablet:flex tablet:h-16 tablet:w-auto tablet:flex-1 tablet:items-center">
                            {stripFlatName(shortName)}
                          </span>
                          <span className="order-3 w-1/2 px-3 text-body-base leading-none text-foreground-primary tablet:order-none tablet:flex tablet:h-16 tablet:w-auto tablet:flex-1 tablet:flex-col tablet:justify-center tablet:gap-1">
                            {formatRoamingVolume(
                              initialAmount,
                              getZoneFromShortName(shortName),
                            )}
                            {isYearlyRoamingPackage(
                              minContractDur,
                              minContractDurUnit,
                            ) && (
                              <span className="block text-body-xs leading-none text-foreground-secondary">
                                {tAdd("365days")}
                              </span>
                            )}
                          </span>
                          <span className="order-4 flex w-1/2 justify-end px-3 text-body-base leading-none text-foreground-primary tablet:order-none tablet:flex tablet:h-16 tablet:w-auto tablet:flex-1 tablet:items-center tablet:justify-start">
                            {getCurrency(Number(price), "CHF")}
                          </span>
                          <span className="order-2 flex w-1/2 items-center justify-end px-3 tablet:order-none tablet:w-14 tablet:justify-center">
                            <RadioIndicator checked={checked} />
                          </span>
                        </div>
                      )}
                    </RadioGroup.Option>
                  ),
                )}
              </RadioGroup>
            )}
          </div>
        </ProfileCard>

        <div className="flex w-full justify-end">
          <Button
            dataTestid="roaming-booking-continue"
            type="submit"
            disabled={selectedRateId === null || isFetchingOrder}
          >
            {capitalizeFirstLetter(portalT("common.buttons.continue"))}
          </Button>
        </div>
      </form>
    </RoamingBookingPage>
  );
};
